import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import ImageUploader from '../../components/ImageUploader';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAR_TYPES = ['SUV', '4x4', 'Berline', 'Break', 'Coupé', 'Cabriolet', 'Monospace'];

export default function AdminCarsPage() {
    const { theme } = useTheme();
    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [editingCar, setEditingCar] = useState(null);
    const [formData, setFormData] = useState({
        marque: '',
        modele: '',
        annee: '',
        kilometrage: '',
        couleur: '',
        puissance_ch: '',
        nb_portes: '',
        nb_places: '',
        type_vehicule: '',
        prix: '',
        images: []
    });

    useEffect(() => {
        fetchCars();
    }, []);

    const fetchCars = async () => {
        try {
            const response = await axios.get(`${API}/cars`);
            setCars(response.data);
        } catch (error) {
            console.error('Error fetching cars:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openCreateDialog = () => {
        setEditingCar(null);
        setFormData({
            marque: '', modele: '', annee: '', kilometrage: '', couleur: '',
            puissance_ch: '', nb_portes: '', nb_places: '', type_vehicule: '', prix: '', images: []
        });
        setShowDialog(true);
    };

    const openEditDialog = (car) => {
        setEditingCar(car);
        setFormData({
            marque: car.marque,
            modele: car.modele,
            annee: car.annee.toString(),
            kilometrage: car.kilometrage.toString(),
            couleur: car.couleur,
            puissance_ch: car.puissance_ch.toString(),
            nb_portes: car.nb_portes.toString(),
            nb_places: car.nb_places.toString(),
            type_vehicule: car.type_vehicule,
            prix: car.prix.toString(),
            images: car.images || []
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            annee: parseInt(formData.annee),
            kilometrage: parseInt(formData.kilometrage),
            puissance_ch: parseInt(formData.puissance_ch),
            nb_portes: parseInt(formData.nb_portes),
            nb_places: parseInt(formData.nb_places),
            prix: parseFloat(formData.prix),
            images: formData.images
        };

        try {
            if (editingCar) {
                await axios.put(`${API}/cars/${editingCar.id}`, payload);
                toast.success('Voiture modifiée');
            } else {
                await axios.post(`${API}/cars`, payload);
                toast.success('Voiture créée');
            }
            setShowDialog(false);
            fetchCars();
        } catch (error) {
            console.error('Error saving car:', error);
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette voiture ?')) return;
        try {
            await axios.delete(`${API}/cars/${id}`);
            toast.success('Voiture supprimée');
            fetchCars();
        } catch (error) {
            console.error('Error deleting car:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    const formatPrice = (price) => new Intl.NumberFormat('fr-SN', { style: 'currency', currency: 'XOF' }).format(price);
    const formatKm = (km) => new Intl.NumberFormat('fr-SN').format(km);

    return (
        <div data-testid="admin-cars-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className={`font-heading text-2xl font-medium ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>Voitures</h1>
                    <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/70' : 'text-mmd-navy/60'}`}>Gérez votre catalogue de véhicules</p>
                </div>
                <Button onClick={openCreateDialog} data-testid="add-car-btn" className={theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'}>
                    <Plus size={18} className="mr-2" />
                    Ajouter
                </Button>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className={`rounded-lg p-4 animate-pulse h-24 ${theme === 'dark' ? 'bg-mmd-surface-dark' : 'bg-white'}`} />
                    ))}
                </div>
            ) : cars.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'text-mmd-silver/50' : 'text-mmd-navy/50'}`}>
                    <p>Aucune voiture</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {cars.map(car => (
                        <div key={car.id} className={`rounded-lg p-4 flex items-center gap-4 border ${theme === 'dark' ? 'bg-mmd-surface-dark border-white/10' : 'bg-white border-black/10'}`} data-testid={`admin-car-${car.id}`}>
                            <img
                                src={car.images?.[0] || 'https://via.placeholder.com/80x80?text=No+Image'}
                                alt={`${car.marque} ${car.modele}`}
                                className="w-20 h-14 rounded-lg object-cover"
                            />
                            <div className="flex-1 min-w-0">
                                <h3 className={`font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-mmd-navy'}`}>{car.marque} {car.modele}</h3>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-silver/60' : 'text-mmd-navy/60'}`}>{car.annee} - {formatKm(car.kilometrage)} km - {car.type_vehicule}</p>
                                <p className={`text-sm ${theme === 'dark' ? 'text-mmd-gold' : 'text-mmd-navy font-semibold'}`}>{formatPrice(car.prix)}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(car)} data-testid={`edit-car-${car.id}`}>
                                    <Pencil size={18} className={theme === 'dark' ? 'text-mmd-silver' : 'text-mmd-navy/60'} />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDelete(car.id)} data-testid={`delete-car-${car.id}`}>
                                    <Trash size={18} className="text-red-500" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingCar ? 'Modifier la voiture' : 'Nouvelle voiture'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="marque">Marque</Label>
                                <Input id="marque" name="marque" value={formData.marque} onChange={handleInputChange} required data-testid="car-form-marque" />
                            </div>
                            <div>
                                <Label htmlFor="modele">Modèle</Label>
                                <Input id="modele" name="modele" value={formData.modele} onChange={handleInputChange} required data-testid="car-form-modele" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="annee">Année</Label>
                                <Input id="annee" name="annee" type="number" value={formData.annee} onChange={handleInputChange} required data-testid="car-form-annee" />
                            </div>
                            <div>
                                <Label htmlFor="kilometrage">Kilométrage</Label>
                                <Input id="kilometrage" name="kilometrage" type="number" value={formData.kilometrage} onChange={handleInputChange} required data-testid="car-form-kilometrage" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="couleur">Couleur</Label>
                                <Input id="couleur" name="couleur" value={formData.couleur} onChange={handleInputChange} required data-testid="car-form-couleur" />
                            </div>
                            <div>
                                <Label htmlFor="puissance_ch">Puissance (ch)</Label>
                                <Input id="puissance_ch" name="puissance_ch" type="number" value={formData.puissance_ch} onChange={handleInputChange} required data-testid="car-form-puissance" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label htmlFor="nb_portes">Portes</Label>
                                <Input id="nb_portes" name="nb_portes" type="number" value={formData.nb_portes} onChange={handleInputChange} required data-testid="car-form-portes" />
                            </div>
                            <div>
                                <Label htmlFor="nb_places">Places</Label>
                                <Input id="nb_places" name="nb_places" type="number" value={formData.nb_places} onChange={handleInputChange} required data-testid="car-form-places" />
                            </div>
                            <div>
                                <Label>Type</Label>
                                <Select value={formData.type_vehicule} onValueChange={(v) => handleSelectChange('type_vehicule', v)}>
                                    <SelectTrigger data-testid="car-form-type">
                                        <SelectValue placeholder="Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CAR_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="prix">Prix (FCFA)</Label>
                            <Input id="prix" name="prix" type="number" value={formData.prix} onChange={handleInputChange} required data-testid="car-form-prix" />
                        </div>
                        <div>
                            <Label className="mb-2 block">Images du véhicule</Label>
                            <ImageUploader 
                                images={formData.images} 
                                onImagesChange={(images) => setFormData(prev => ({ ...prev, images }))}
                                maxImages={10}
                            />
                        </div>
                        <Button type="submit" className={`w-full ${theme === 'dark' ? 'bg-mmd-gold text-mmd-dark-bg hover:bg-mmd-gold-hover' : 'bg-mmd-navy text-white hover:bg-mmd-navy-hover'}`} data-testid="car-form-submit">
                            {editingCar ? 'Enregistrer' : 'Créer'}
                        </Button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
